import { Alert } from '@warp-ds/react';
import { type ReactNode, useMemo } from 'react';

import { t } from '../../locale/texts';
import { CustomNumberInput } from '../common/CustomNumberInput';
import { Dropdown, type DropdownProps } from '../common/Dropdown';

const MAX_TOTAL_PASSENGERS = 9;

export type PassengersProps = {
    form: {
        adults: number;
        children: number;
        infants: number;
    };
    setPassenger: (value: Record<string, number>) => void;
    className?: string;
    trigger?: DropdownProps['trigger'];
    type?: 'dropdown' | 'inline';
};

export const Passengers = ({ type, form, setPassenger, trigger, className }: PassengersProps) => {
    const { adults, children, infants } = form;
    const totalPassengers = adults + children + infants;
    const hasReachedMax = totalPassengers >= MAX_TOTAL_PASSENGERS;

    const passengerInputs = useMemo(
        () => [
            {
                id: 'adultCount',
                value: adults,
                label: t('flight.passengers.inputs.adultLabel'),
                helpText: t('flight.passengers.inputs.adultShortDescription'),
                min: 1,
                disableAdd: hasReachedMax,
                disableSubtract: adults <= 1,
                setNumber: (val: number) => setPassenger({ adults: val }),
            },
            {
                id: 'childCount',
                value: children,
                label: t('flight.passengers.inputs.childLabel'),
                helpText: t('flight.passengers.inputs.childDescription'),
                min: 0,
                disableAdd: hasReachedMax,
                disableSubtract: children <= 0,
                setNumber: (val: number) => setPassenger({ children: val }),
            },
            {
                id: 'infantCount',
                value: infants,
                label: t('flight.passengers.inputs.infantLabel'),
                helpText: t('flight.passengers.inputs.infantDescription'),
                min: 0,
                disableAdd: hasReachedMax,
                disableSubtract: infants <= 0,
                setNumber: (val: number) => setPassenger({ infants: val }),
            },
        ],
        [adults, children, infants, hasReachedMax, setPassenger],
    );

    return (
        <PassengersType type={type} className={className} trigger={trigger}>
            <fieldset className="flex flex-col gap-16">
                <legend className={type === 'dropdown' ? 'sr-only' : 't4'}>Reisende</legend>
                {passengerInputs.map((input) => (
                    <CustomNumberInput key={input.id} {...input} />
                ))}
            </fieldset>
            {hasReachedMax && (
                <Alert type="info" show>
                    {t('flight.passengers.inputs.countWarning')}
                </Alert>
            )}
        </PassengersType>
    );
};

type PassengersTypeProps = {
    children: ReactNode;
} & Pick<PassengersProps, 'trigger' | 'className' | 'type'>;

const PassengersType = ({ children, trigger, className, type }: PassengersTypeProps) => {
    if (type === 'dropdown') {
        return (
            <Dropdown trigger={trigger ?? { text: '' }} className={className} slim>
                {children}
            </Dropdown>
        );
    }

    return <>{children}</>;
};
